import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as subscriptionApi from '../services/subscriptionApi';
import type {
  TierLimits,
  Usage,
} from '../services/subscriptionApi';

// Query keys
const QUERY_KEYS = {
  subscription: ['subscription'] as const,
  usage: ['subscription', 'usage'] as const,
  invoices: ['subscription', 'invoices'] as const,
  paymentMethod: ['subscription', 'payment-method'] as const,
};

/**
 * Hook to get subscription data
 */
export function useSubscription() {
  return useQuery({
    queryKey: QUERY_KEYS.subscription,
    queryFn: subscriptionApi.getSubscription,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to create a checkout session
 */
export function useCreateCheckout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tier,
      billingCycle,
      successUrl,
      cancelUrl,
    }: {
      tier: 'launch' | 'rise' | 'summit';
      billingCycle: 'monthly' | 'annual';
      successUrl?: string;
      cancelUrl?: string;
    }) => subscriptionApi.createCheckout(tier, billingCycle, successUrl, cancelUrl),
    onSuccess: () => {
      // Invalidate subscription data after checkout
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscription });
    },
  });
}

/**
 * Hook to create a portal session
 */
export function useCreatePortalSession() {
  return useMutation({
    mutationFn: (returnUrl?: string) =>
      subscriptionApi.createPortalSession(returnUrl),
  });
}

/**
 * Hook to get invoices
 */
export function useInvoices(limit?: number) {
  return useQuery({
    queryKey: [...QUERY_KEYS.invoices, limit],
    queryFn: () => subscriptionApi.getInvoices(limit),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to get payment method
 */
export function usePaymentMethod() {
  return useQuery({
    queryKey: QUERY_KEYS.paymentMethod,
    queryFn: subscriptionApi.getPaymentMethod,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to preview plan change
 */
export function usePreviewPlanChange() {
  return useMutation({
    mutationFn: ({
      tier,
      billingCycle,
    }: {
      tier: 'launch' | 'rise' | 'summit';
      billingCycle: 'monthly' | 'annual';
    }) => subscriptionApi.previewPlanChange(tier, billingCycle),
  });
}

/**
 * Hook to cancel subscription
 */
export function useCancelSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => subscriptionApi.cancelSubscription(),
    onSuccess: () => {
      // Invalidate subscription data after cancellation
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscription });
    },
  });
}

/**
 * Helper: Check if a specific limit is reached
 */
export function isLimitReached(
  usage: Usage,
  limits: TierLimits,
  limitType: keyof Usage
): boolean {
  const limit = getLimitForUsageType(limits, limitType);
  if (limit === -1) return false; // Unlimited
  return usage[limitType] >= limit;
}

/**
 * Helper: Get usage percentage for a specific limit
 */
export function getUsagePercentage(
  usage: Usage,
  limits: TierLimits,
  limitType: keyof Usage
): number {
  const limit = getLimitForUsageType(limits, limitType);
  if (limit === -1) return 0; // Unlimited shows as 0%
  if (limit === 0) return 100; // Expired tier
  return Math.min(100, (usage[limitType] / limit) * 100);
}

/**
 * Map usage type to limit type
 */
function getLimitForUsageType(
  limits: TierLimits,
  usageType: keyof Usage
): number {
  const mapping: Record<keyof Usage, keyof TierLimits> = {
    clients: 'clients',
    projects: 'projects',
    tasks: 'tasks',
    notes: 'notes',
    templates: 'templates',
    landingPages: 'landingPages',
    invoicesMonthly: 'invoicesMonthly',
    contractsMonthly: 'contractsMonthly',
    leadsMonthly: 'leadsMonthly',
    aiActionsMonthly: 'aiActionsMonthly',
    storageUsedBytes: 'storageGb',
  };

  const limitKey = mapping[usageType];
  const limit = limits[limitKey];

  // Handle storageGb conversion (limit is in GB, usage is in bytes)
  if (usageType === 'storageUsedBytes' && typeof limit === 'number') {
    return limit * 1024 * 1024 * 1024; // Convert GB to bytes
  }

  return typeof limit === 'number' ? limit : 0;
}

/**
 * Format storage for display
 */
export function formatStorage(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Get tier display name
 */
export function getTierDisplayName(tier: string): string {
  const names: Record<string, string> = {
    trial: 'Trial',
    launch: 'Launch',
    rise: 'Rise',
    summit: 'Summit',
    expired: 'Expired',
  };
  return names[tier] || tier;
}

/**
 * Get status display info
 */
export function getStatusInfo(status: string): { label: string; color: 'success' | 'warning' | 'error' | 'info' } {
  const info: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'info' }> = {
    trialing: { label: 'Trial', color: 'info' },
    active: { label: 'Active', color: 'success' },
    past_due: { label: 'Past Due', color: 'warning' },
    canceled: { label: 'Canceled', color: 'error' },
    unpaid: { label: 'Unpaid', color: 'error' },
    expired: { label: 'Expired', color: 'error' },
  };
  return info[status] || { label: status, color: 'info' };
}

export default {
  useSubscription,
  useCreateCheckout,
  useCreatePortalSession,
  useInvoices,
  usePaymentMethod,
  usePreviewPlanChange,
  useCancelSubscription,
  isLimitReached,
  getUsagePercentage,
  formatStorage,
  getTierDisplayName,
  getStatusInfo,
};
